"use client";

import { useState } from "react";
import { FlowInput } from "@/components/get-started/FlowInput";

function AuthShell({ onSubmit, children }: { onSubmit?: (e: React.FormEvent) => void; children: React.ReactNode }) {
  return (
    <div className="relative isolate min-h-[calc(100vh-80px)] w-full bg-black">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_66%_40%_at_4%_106%,rgba(0,97,254,0.70)_0%,rgba(0,97,254,0.24)_50%,transparent_80%),radial-gradient(ellipse_66%_40%_at_96%_106%,rgba(0,97,254,0.70)_0%,rgba(0,97,254,0.24)_50%,transparent_80%)]"
      />
      <form
        onSubmit={onSubmit}
        className="mx-auto flex min-h-[70vh] w-full max-w-[440px] flex-col justify-center gap-4 px-4 py-24"
      >
        {children}
      </form>
    </div>
  );
}

const API_URL = process.env.NEXT_PUBLIC_DASHBOARD_API_URL ?? "";
const DASHBOARD_URL = (process.env.NEXT_PUBLIC_DASHBOARD_URL ?? "").replace(/\/$/, "");

/**
 * Client login. Signs in through the MuteTaxes dashboard API (/auth/login), then sends the browser to
 * the dashboard, which starts its own session from the tokens.
 */
function LoginPanel() {
  const [hint, setHint] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      // This page is for clients; admins sign in on the dashboard itself.
      if (res.ok && data.success && data.user?.type === "ADMIN") {
        setError("Please sign in to the admin dashboard directly.");
        setSubmitting(false);
        return;
      }
      if (!res.ok || !data.accessToken || !data.refreshToken) {
        setError(
          
            "Invalid email or password."
        );
        // setError(
        //   res.status === 401 || res.status === 400
        //     ? "Invalid email or password."
        //     : "We couldn't sign you in right now. Please try again.",
        // );
        setSubmitting(false);
        return;
      }
      // Tokens go in the URL fragment, which browsers never send to a server.
      const hash = new URLSearchParams({ at: data.accessToken, rt: data.refreshToken });
      window.location.assign(`${DASHBOARD_URL}/auth/handoff#${hash}`);
    } catch {
      setError("We couldn't reach the server. Please check your connection and try again.");
      setSubmitting(false);
    }
  }

  return (
    <AuthShell onSubmit={handleSubmit}>
        <h1 className="font-suisse-intl-bold text-3xl text-white sm:text-4xl">Log in</h1>
        <p className="mb-2 font-suisse-intl-medium text-base text-white/70">
          Sign in to your MuteTaxes dashboard.
        </p>

        <FlowInput
          id="login-email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="Email address"
          autoComplete="email"
          inputMode="email"
          ariaLabel="Email address"
          required
        />
        <FlowInput
          id="login-password"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="Password"
          autoComplete="current-password"
          ariaLabel="Password"
          required
        />

        {error && (
          <p role="alert" className="font-suisse-intl-medium text-sm text-red-400">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !email || !password}
          className="mt-2 flex h-12 items-center justify-center rounded-full bg-white px-8 font-suisse-intl text-lg text-[#2A4775] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? "Signing in…" : "Log in"}
        </button>

        <a
          href={`${DASHBOARD_URL}/forgot-password`}
          className="text-center font-suisse-intl-medium text-sm text-white/70 underline-offset-4 hover:text-white hover:underline"
        >
          Forgot your password?
        </a>

        <p className="text-center font-suisse-intl-medium text-sm text-white/70">
          Don&apos;t have an account?{" "}
          <button
            type="button"
            onClick={() => {
              setHint(true);
              window.scrollTo({ top: 0, behavior: "smooth" });
              window.dispatchEvent(new Event(HIGHLIGHT_GET_STARTED_EVENT));
            }}
            className="text-white underline underline-offset-4 hover:opacity-80"
          >
            Register
          </button>
        </p>
        {hint && (
          <p role="status" className="text-center font-suisse-intl-medium text-sm text-[#6aa6ff]">
            Click <span className="font-semibold">Get started</span> at the top to register. We&apos;ll email you
            your dashboard login when you&apos;re done.
          </p>
        )}
    </AuthShell>
  );
}

/** Event the nav listens for to draw attention to its "Get started" button. */
export const HIGHLIGHT_GET_STARTED_EVENT = "mutetaxes:highlight-get-started";

/** The /login page. New clients register through the Get started flow, so "Register" points there. */
export function LoginForm() {
  return <LoginPanel />;
}
